import { FunctionComponent, ReactNode } from "react";

import {
	faChevronDoubleDown,
	faChevronDoubleUp,
	faColumns3,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { PersonSummary } from "./PersonSummary";

import { Organization, People } from "../../../Apollo/schema/get-people";
import { ColumnValue } from "../../../List/schema/columnValue";
import { LeadInfoBox } from "../../../Messages/components/LeadInfoBox";
import { ListPerson } from "../../../List/schema/list";
import { ListSourceDataSection } from "../../../List/pages/Unassigned/components/ListSourceDataSection";
import { LeadScoringSection } from "../../../List/pages/Unassigned/components/LeadScoringSection";

interface OverviewTabProps {
	/** Accepted for call-site compatibility; company details render in the CompanyHeader card. */
	organization?: Organization;
	primaryPerson?: People;
	hasPersons: boolean;
	columns?: ColumnValue[];
	listPerson?: ListPerson;
	/**
	 * Optional content rendered directly below the Summary and above {@link belowSummary} — the
	 * "Recent Signals & Activities" box lives here (mockup order: Summary → Recent → In lists).
	 */
	afterSummary?: ReactNode;
	/** Optional content rendered directly below the Summary (e.g. the "Also on these lists" block). */
	belowSummary?: ReactNode;
}

export const OverviewTab: FunctionComponent<OverviewTabProps> = ({
	primaryPerson,
	hasPersons,
	columns,
	listPerson,
	afterSummary,
	belowSummary,
}) => {
	return (
		<div className="flex flex-col gap-3.5">
			{/* The company details (about, HQ, other offices, employees, industry) live in the
			    CompanyHeader identity card's "See more" expander; the person's headline + bio in
			    the person card's "See work history" expander. Here the bio only leads the tab as
			    a plain clamped Summary (v33 mockup). */}
			{hasPersons &&
				primaryPerson &&
				!["SELECT", "Person not found", "Company not found"].includes(primaryPerson.name ?? "") && (
					<PersonSummary text={primaryPerson.about} />
				)}

			{afterSummary}

			{belowSummary}

			{/* Tracker Columns Section */}
			{columns && columns.length > 0 && (
				<details
					className="flex group w-full flex-col border border-gray-200 rounded-lg p-4"
					open={false}
				>
					<summary className="cursor-pointer flex bg-white flex-row items-start w-full justify-between gap-2">
						<h3 className="text-sm font-semibold text-gray-900">Tracker Data ({columns.length})</h3>
						<div className="flex flex-row gap-2 justify-center items-center text-sm group-open:hidden">
							<p>Show More</p>
							<Icon className="cursor-pointer text-sm w-3 h-3" icon={faChevronDoubleDown} />
						</div>
						<div className="flex flex-row gap-2 justify-center items-center text-sm hidden group-open:flex">
							<p>Show Less</p>
							<Icon className="cursor-pointer text-sm w-3 h-3" icon={faChevronDoubleUp} />
						</div>
					</summary>
					<div className="w-full mt-4 flex flex-col gap-1">
						{columns.map((column) => {
							let customName = column.columnConfig?.name || "Column";
							const isCamelCase = /^[a-z]+([A-Z][a-z]*)+$/.test(customName);

							if (isCamelCase) {
								customName = customName
									.replace(/([A-Z])/g, " $1")
									.replace(/^./, (char) => char.toUpperCase());
							}

							return (
								<LeadInfoBox
									key={column.id}
									IconElement={
										<div className="flex h-5 min-w-5">
											<Icon className="text-gray-400" icon={faColumns3} />
										</div>
									}
									content={
										column.value?.answer !== undefined && column.value?.answer !== null ? (
											<div className="flex gap-2">
												<p className="font-semibold">{`${customName}:`}</p>
												<p>{`${typeof column.value?.answer === "object" ? JSON.stringify(column.value?.answer) : column.value?.answer}${column?.answerExplanation ? `\n${column.answerExplanation}` : ""}`}</p>
											</div>
										) : (
											<div className="flex gap-2">
												<p className="font-semibold">{`${customName}:`}</p>
												<p>{`${typeof column.value === "object" && column.value !== null ? JSON.stringify(column.value) : (column.value ?? "")}`}</p>
											</div>
										)
									}
									url={column.sourceUrl ?? undefined}
								/>
							);
						})}
					</div>
				</details>
			)}

			{/* List Source Data (posts, events, maps, etc.) */}
			{listPerson && <ListSourceDataSection listPerson={listPerson} />}

			{/* Lead Scoring Reasoning */}
			{listPerson && <LeadScoringSection listPerson={listPerson} />}
		</div>
	);
};
